import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Link } from 'react-router-dom';
import { PATH } from '@/router/PATH';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type JournalEntryLinkProps = {
    /** The entry the document posted, or null when it posted none. */
    entryId: string | null | undefined;
    /**
     * The document's own accounting date (ISO `YYYY-MM-DD`). The journal is a
     * windowed list, so a deep link has to say which window contains the entry
     * — passing the document's date on both sides always does.
     */
    date: string;
    /** testid slot; the document id goes on the end. */
    slot: string;
};

/**
 * A link from a document to the journal entry that books it.
 *
 * Sales and received purchase invoices each post one entry (BOS-119), and the
 * only useful thing to say about it from a receipt or an invoice row is "here
 * is where this landed in the books". Renders nothing when there is no entry —
 * which today means a document recorded before operations were wired to the
 * ledger, since a trade the books refuse is no longer recorded at all.
 */
export const JournalEntryLink: React.FC<JournalEntryLinkProps> = ({
    entryId,
    date,
    slot,
}) => {
    const { t } = useTranslation('components');

    if (!entryId) {
        return null;
    }

    const day = date.slice(0, 10);

    return (
        <Link
            className="inline-flex items-center gap-1 text-sm text-accent underline-offset-2 hover:underline"
            to={PATH.ACCOUNTING.BOOKS.BIND_JOURNAL_FOR_ENTRY(entryId, {
                from: day,
                to: day,
            })}
            {...getNodeTestIdProp(`${slot}-journal-entry-link`)}
        >
            {t('journalEntryLink.label')}
            <Icon
                aria-hidden
                className="size-3.5"
                icon="gravity-ui:arrow-up-right-from-square"
            />
        </Link>
    );
};
